import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as errorCodes from '../common/errorCodes';
import * as loginactions from '../loginpages/actionCreators';
import { authCredential } from './actionCreators';
import LoginTemplateTfaHelpPages from '../loginpages/LoginTemplateTfaHelpPages';
import { useJspVariables, useNavigation, useAppContext, useAuth, useLoader, useDebugRender } from '../hooks';
import { IceIcon, PageContainer, PageHeader } from '../components';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';

const PageUpdateTfaLogin = () => {
    useDebugRender('PageUpdateTfaLogin');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { panelStyle, launcher, jspVariables } = useJspVariables();
    const { getLabelFromJspVariables } = useAppContext(jspVariables);
    const { toPageUserInfo } = useNavigation();

    const { authenticatedUser, authenticatedApp } = useAuth();
    const { showLoader, hideLoader } = useLoader();
    const isUserAuthenticated = !!authenticatedUser;

    const [userName, setUserName] = useState(authenticatedUser?.authenticatedUser || '');
    const isGlobalIdUser = userName.includes('@');
    const appKey = isGlobalIdUser ? 'GLOBAL' : (authenticatedApp || '');
    const [password, setPassword] = useState('');
    const [tfaPasscode, setTfaPasscode] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [alert, setAlert] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const onPasswordChange = (event) => {
        setPassword(event.target.value);
        if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: '' }));
    };

    const onTfaPasscodeChange = (event) => {
        setTfaPasscode(event.target.value);
        if (fieldErrors.tfaPasscode) setFieldErrors(prev => ({ ...prev, tfaPasscode: '' }));
    };

    const validate = (requireOtp) => {
        const errors = {};
        if (!userName) errors.userName = errorCodes.MSG_FIELD_REQUIRED();
        if (!password) errors.password = errorCodes.MSG_FIELD_REQUIRED();
        if (requireOtp && !tfaPasscode) errors.tfaPasscode = errorCodes.MSG_FIELD_REQUIRED();
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const navigateToTfaRegister = (skipTfaSessionCheck = false) => {
        navigate(constants.ROUTE_PAGE_TFA_REGISTER, { state: { skipTfaSessionCheck } });
    };

    const authenticate = async () => {
        if (!validate(true)) return;
        setAlert(null);
        showLoader('Authenticating...');
        try {
            const resp = await loginactions.authenticateTfaAsync(userName, appKey, password, tfaPasscode);
            dispatch({ type: loginactions.AUTH_SESSION, data: {
                authenticatedUser: resp.data,
                isAuthenticated: true,
                authenticatedApp: resp.data.authenticatedApp || appKey,
            }});
            navigateToTfaRegister(true);
        } catch (resp) {
            setAlert({ message: resp.message || getLabelFromJspVariables('err_authentication_failed'), severity: 'error' });
        } finally {
            hideLoader();
        }
    };

    const handle2FAAuthentication = async () => {
        if (!validate(false)) return;
        setAlert(null);
        showLoader('Sending 2FA passcode...');
        try {
            const authResp = await loginactions.authenticateTfaAsync(userName, appKey, password);
            // Code 0 — device trusted, user verified. Go directly to TFA settings.
            dispatch({ type: loginactions.AUTH_SESSION, data: {
                authenticatedUser: authResp.data,
                isAuthenticated: true,
                authenticatedApp: authResp.data.authenticatedApp || appKey,
            }});
            navigateToTfaRegister(true);
        } catch (resp) {
            if (resp.code == errorCodes.ERR_REQUIRES_TFA && resp.data?.webauthn) {
                // WebAuthn user — trigger fingerprint/Yubikey prompt
                try {
                    const webauthnData = JSON.parse(resp.data.webauthn);
                    setAlert({ message: resp.data.userMessage || getLabelFromJspVariables('note_follow_screen_passkey'), severity: 'success' });
                    const publicKey = await authCredential(webauthnData.credentialCreateJson);
                    const webauthnCredential = {
                        userName: webauthnData.user,
                        appKey: webauthnData.app,
                        requestId: webauthnData.requestId,
                        publicKey,
                    };
                    const webauthnResp = await loginactions.authenticateTfaAsync(userName, appKey, password, 'webAuthn', webauthnCredential);
                    dispatch({ type: loginactions.AUTH_SESSION, data: {
                        authenticatedUser: webauthnResp.data,
                        isAuthenticated: true,
                        authenticatedApp: webauthnResp.data.authenticatedApp || appKey,
                    }});
                    navigateToTfaRegister(true);
                } catch (webauthnError) {
                    setAlert({ message: webauthnError.message || getLabelFromJspVariables('err_webauthn_auth_failed'), severity: 'error' });
                }
            } else if (resp.code == errorCodes.ERR_REQUIRES_TFA) {
                setFieldErrors({});
                setOtpSent(true);
                const deliveryPref = resp.data?.tfaDeliveryPref;
                if (deliveryPref === 'ICE_MOBILE_APP') {
                    setAlert({ message: getLabelFromJspVariables('note_enter_authenticator_code'), severity: 'success' });
                } else {
                    setAlert({ message: resp.data?.userMessage || getLabelFromJspVariables('otp_sent_enter_otp') || '2FA Passcode sent to your email', severity: 'success' });
                }
            } else {
                setAlert({ message: resp.message, severity: 'error' });
            }
        } finally {
            hideLoader();
        }
    };

    const isTfaPasscodeVisible = otpSent;

    return (
        <PageContainer id="pageUpdateTfaLogin" dataAutomationId="page_update_tfa_login">
            <Box sx={panelStyle}>
                <Grid xs={12} display="flex" justifyContent="flex-end">
                    <Button variant="text" onClick={() => toPageUserInfo()}
                        data-automation-id="update_tfa_login_back_btn">{getLabelFromJspVariables('action_page_back')}</Button>
                </Grid>
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="update_tfa_login_header">{getLabelFromJspVariables('login_to_update')}</PageHeader>
                <Box component="form" id="formUpdate2FASettingsLogin"
                    data-automation-id="form_update2_fa_settings_login"
                    onSubmit={e => { e.preventDefault(); isTfaPasscodeVisible ? authenticate() : handle2FAAuthentication(); }}>
                    {alert && <Alert id="pageUpdateTfaLoginAlert" data-automation-id="page_update_tfa_login_alert" severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                    {!isTfaPasscodeVisible && !alert && <Alert severity="info" aria-live="polite" sx={{ mb: 1 }}>{getLabelFromJspVariables('enter_username_check_new_passcode')}</Alert>}
                    <LoginTemplateTfaHelpPages
                        userName={userName}
                        userNameDisabled={isUserAuthenticated}
                        password={password}
                        tfaPasscode={tfaPasscode}
                        userNameLabel={getLabelFromJspVariables(userName.includes('@') ? 'label_email' : 'label_userid')}
                        tfaPasscodeLabel={getLabelFromJspVariables('label_tfa_passcode')}
                        onUserNameChange={(e) => setUserName(e.target.value)}
                        onPasswordChange={onPasswordChange}
                        onTfaPasscodeChange={onTfaPasscodeChange}
                        showTfaPasscode={isTfaPasscodeVisible}
                        userNameError={fieldErrors.userName}
                        passwordError={fieldErrors.password}
                        tfaPasscodeError={fieldErrors.tfaPasscode}
                    />
                    <Box sx={{ mt: 2 }}>
                    {
                        !isTfaPasscodeVisible
                            ?
                                <Button variant="contained" fullWidth type="submit"
                                    data-automation-id="update_tfa_login_send_otp_btn">
                                    {getLabelFromJspVariables('action_send_otp')}
                                </Button>
                            :
                            <Button variant="contained" fullWidth type="submit"
                                data-automation-id="update_tfa_login_submit_btn">
                                {getLabelFromJspVariables('login_to_update')}
                            </Button>
                    }
                    </Box>
                </Box>
            </Box>
        </PageContainer>
    );
};

export default PageUpdateTfaLogin;
